import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { GetReportZ, GetReportZXlsx } from "../../api/report";
import ZReportChart from "./ZReportChart";

const MAX_PERIOD_DAYS = 40;

function errorText(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (typeof payload.detail === "string") return payload.detail;
  if (Array.isArray(payload.detail) && payload.detail[0]?.msg) {
    return payload.detail[0].msg;
  }
  return fallback;
}

function formatIso(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDefaultEnd() {
  return formatIso(new Date());
}

function getDefaultStart() {
  const date = new Date();
  date.setDate(date.getDate() - 6);
  return formatIso(date);
}

function formatDay(value) {
  if (!value) return "—";
  const iso = String(value).slice(0, 10);
  const [, month, day] = iso.split("-");
  return day && month ? `${day}.${month}` : iso;
}

function formatAmount(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return number.toLocaleString("ru-RU", { maximumFractionDigits: 0 });
}

function formatCell(value) {
  if (value == null || value === "" || Number(value) === 0) return "";
  return formatAmount(value);
}

const ReportZ = ({ token }) => {
  const [startDate, setStartDate] = useState(getDefaultStart);
  const [endDate, setEndDate] = useState(getDefaultEnd);
  const [report, setReport] = useState(null);
  const [loadingMode, setLoadingMode] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState("");

  const isBusy = loadingMode !== null;

  useEffect(() => {
    if (!loadingMode) {
      setElapsedSeconds(0);
      return undefined;
    }

    setElapsedSeconds(0);
    const interval = setInterval(() => {
      setElapsedSeconds((seconds) => seconds + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [loadingMode]);

  const validatePeriod = () => {
    if (!startDate || !endDate) {
      return "Укажите начало и конец периода";
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) {
      return "Конец периода не может быть раньше начала";
    }
    const days = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
    if (days > MAX_PERIOD_DAYS) {
      return `Период не должен превышать ${MAX_PERIOD_DAYS} дней`;
    }
    return "";
  };

  async function loadReport() {
    const validationError = validatePeriod();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoadingMode("view");
    setError("");
    try {
      const response = await GetReportZ(token, startDate, endDate);
      if (response?.detail) {
        setError(errorText(response, "Не удалось загрузить отчёт"));
        setReport(null);
        return;
      }
      setReport(response);
    } finally {
      setLoadingMode(null);
    }
  }

  async function downloadReport() {
    const validationError = validatePeriod();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoadingMode("download");
    setError("");
    try {
      const response = await GetReportZXlsx(token, startDate, endDate);
      if (!response?.data) {
        setError(errorText(response?.data, "Не удалось скачать отчёт"));
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let filename = "z_report.xlsx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/\.xlsx_?[^.]*$/, ".xlsx");
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } finally {
      setLoadingMode(null);
    }
  }

  const stats = useMemo(() => {
    const restaurants = report?.restaurants || [];
    if (!restaurants.length) return null;
    let cash = 0;
    let ecash = 0;
    for (const restaurant of restaurants) {
      cash += Number(restaurant.total_cash) || 0;
      ecash += Number(restaurant.total_ecash) || 0;
    }
    return {
      restaurants: restaurants.length,
      days: report?.dates?.length || 0,
      cash,
      ecash,
      total: cash + ecash,
    };
  }, [report]);

  const loadingLabel =
    loadingMode === "download" ? "Скачивание отчёта" : "Загрузка отчёта";

  return (
    <div className="z-report-page">
      {isBusy ? (
        <div
          className="z-report-loading-overlay"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="z-report-loading-panel">
            <div className="spinner-border profile-spinner" role="status">
              <span className="visually-hidden">{loadingLabel}</span>
            </div>
            <p className="z-report-loading-title">{loadingLabel}…</p>
            <p className="z-report-loading-time">{elapsedSeconds} сек.</p>
            <p className="z-report-loading-hint">
              Данные запрашиваются из OFD — это может занять некоторое время
            </p>
          </div>
        </div>
      ) : null}

      <div className="reports-header">
        <div>
          <h1>Z-отчёт (OFD)</h1>
          <p className="reports-hint">
            Наличные и безналичные суммы смены по дате открытия, за вычетом
            возвратов. Если кассу закрыли после полуночи, деньги остаются в
            дне открытия. Период —
            не больше {MAX_PERIOD_DAYS} дней
          </p>
        </div>
        <Link to="/reports" className="reports-back-link">
          ← К отчётам
        </Link>
      </div>

      <form
        className="users-filters z-report-filters"
        onSubmit={(event) => {
          event.preventDefault();
          loadReport();
        }}
      >
        <label className="users-field">
          <span>Период с</span>
          <input
            className="form-control"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </label>
        <label className="users-field">
          <span>Период по</span>
          <input
            className="form-control"
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </label>
        <div className="z-report-filters-actions">
          <button
            type="submit"
            className="btn login-submit"
            disabled={isBusy}
          >
            {loadingMode === "view" ? "Загрузка…" : "Показать"}
          </button>
          <button
            type="button"
            className="z-report-xlsx"
            onClick={downloadReport}
            disabled={isBusy}
          >
            {loadingMode === "download" ? "Скачивание…" : "Скачать .xlsx"}
          </button>
        </div>
      </form>

      {error ? <div className="login-error">{error}</div> : null}

      {report ? (
        <>
          {stats ? (
            <div className="chk-summary-kpis z-report-kpis">
              <div className="chk-summary-kpi">
                <span>Рестораны</span>
                <strong>{stats.restaurants}</strong>
              </div>
              <div className="chk-summary-kpi">
                <span>Дней</span>
                <strong>{stats.days}</strong>
              </div>
              <div className="chk-summary-kpi">
                <span>Наличные</span>
                <strong className="sn-kpi-text">{formatAmount(stats.cash)}</strong>
              </div>
              <div className="chk-summary-kpi">
                <span>Безнал</span>
                <strong className="sn-kpi-text">{formatAmount(stats.ecash)}</strong>
              </div>
              <div className="chk-summary-kpi">
                <span>Всего</span>
                <strong className="sn-kpi-text">{formatAmount(stats.total)}</strong>
              </div>
            </div>
          ) : null}

          <section className="users-list z-report-card">
            <div className="users-list-head">
              <h2>Сводка по дням</h2>
              <span className="users-count">
                {formatDay(report.start_date)} — {formatDay(report.end_date)}
              </span>
            </div>
            <div className="z-report-table-wrap">
              <table className="z-report-table">
                <thead>
                  <tr>
                    <th rowSpan={2} className="z-report-sticky">
                      Ресторан
                    </th>
                    {report.date_labels?.map((label) => (
                      <th
                        key={label}
                        colSpan={2}
                        className="z-report-day-head"
                      >
                        {label}
                      </th>
                    ))}
                    <th colSpan={2} className="z-report-day-head z-report-total">
                      Итого
                    </th>
                  </tr>
                  <tr>
                    {report.dates?.map((dayKey) => (
                      <React.Fragment key={dayKey}>
                        <th className="z-report-sub">Нал</th>
                        <th className="z-report-sub">Безнал</th>
                      </React.Fragment>
                    ))}
                    <th className="z-report-sub z-report-total">Нал</th>
                    <th className="z-report-sub z-report-total">Безнал</th>
                  </tr>
                </thead>
                <tbody>
                  {report.restaurants?.map((restaurant, index) => (
                    <tr
                      key={restaurant.restaurant_id}
                      className={
                        index % 2 === 0 ? "z-report-row-even" : "z-report-row-odd"
                      }
                    >
                      <td className="z-report-sticky">
                        {restaurant.restaurant_name}
                      </td>
                      {restaurant.days?.map((day) => (
                        <React.Fragment key={day.date}>
                          <td className="z-report-num">{formatCell(day.cash)}</td>
                          <td className="z-report-num">{formatCell(day.ecash)}</td>
                        </React.Fragment>
                      ))}
                      <td className="z-report-num z-report-total">
                        {formatAmount(restaurant.total_cash)}
                      </td>
                      <td className="z-report-num z-report-total">
                        {formatAmount(restaurant.total_ecash)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <ZReportChart report={report} />
        </>
      ) : (
        <div className="users-empty">
          Выберите период и нажмите «Показать». Данные придут из OFD
        </div>
      )}
    </div>
  );
};

export default ReportZ;
